import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useState as _unused } from "framer-motion";
import { useState } from "react";
import {
  Globe2,
  Rocket,
  ShoppingCart,
  LayoutTemplate,
  AppWindow,
  Gauge,
  ShieldCheck,
  Search,
  Layers,
  Code2,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// ================= THEME =================
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#0E0709",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

// ================= DATA =================
const stats = [
  { n: "120+", t: "Projects Delivered" },
  { n: "60+", t: "Happy Clients" },
  { n: "5+ Years", t: "Experience" },
  { n: "99%", t: "Retention" },
];

const solutions = [
  { no: "01", icon: Globe2, title: "Business Websites", desc: "Fast, professional sites that establish trust and convert visitors into leads." },
  { no: "02", icon: ShoppingCart, title: "E-commerce Stores", desc: "Scalable online storefronts built for checkout speed and conversion." },
  { no: "03", icon: AppWindow, title: "Web Applications", desc: "Custom-built applications tailored to your exact business workflow." },
  { no: "04", icon: LayoutTemplate, title: "Landing Pages", desc: "Conversion-optimized pages designed to turn clicks into customers." },
];

const features = [
  { icon: Rocket, title: "Modern Stack", desc: "Future-ready technologies — React, Next.js and Node — built to scale without a rewrite.", highlight: true },
  { icon: Gauge, title: "High Performance", desc: "Optimized builds delivering fast load times and smooth interactions." },
  { icon: Search, title: "SEO Optimized", desc: "Fast loading, semantic, and search-friendly from the first commit." },
  { icon: ShieldCheck, title: "Secure by Default", desc: "Best-practice security baked into every build, not bolted on after." },
  { icon: Layers, title: "Responsive Design", desc: "Pixel-perfect across desktop, tablet and mobile, every time." },
  { icon: Code2, title: "Clean Codebase", desc: "Maintainable, documented code your team can build on for years." },
];

const techStack = ["React", "Next.js", "Node.js", "MongoDB", "Tailwind", "AWS"];

const process = [
  { step: "Planning", desc: "Requirements & strategy" },
  { step: "Design", desc: "UI/UX & wireframes" },
  { step: "Development", desc: "Clean scalable code" },
  { step: "Launch", desc: "Testing & deployment" },
];

const faqs = [
  { q: "How long does a website take?", a: "Most business sites ship in 3-6 weeks; larger web apps typically run 8-12 weeks depending on scope." },
  { q: "Do you provide maintenance?", a: "Yes — ongoing support and maintenance plans are available after launch." },
  { q: "Is SEO included?", a: "Every build ships SEO-optimized by default, with deeper technical SEO available as an add-on." },
];

const solutionAccents = ["#E63946", "#D9A441", "#E63946", "#D9A441"];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${center ? "mx-auto" : ""}`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// Hero background — CodeRain: vertical columns of monospace glyphs
// falling and fading (Matrix-style, but sparse and tinted red/gold to
// match the site), a horizontal scan-beam sweeping top to bottom, and
// a faint circuit-trace grid. No floating card/device mock — the
// background itself is the whole visual.
// ---------------------------------------------------------------------
const rainChars = "01{}<>/=;+-*[]()#$".split("");
const rainColumns = Array.from({ length: 22 }, (_, i) => ({
  left: (i / 22) * 100 + Math.random() * 2,
  duration: 6 + Math.random() * 8,
  delay: Math.random() * 8,
  color: i % 3 === 0 ? theme.accent : i % 3 === 1 ? theme.gold : "#FFFFFF",
  chars: Array.from({ length: 10 }, () => rainChars[Math.floor(Math.random() * rainChars.length)]),
}));

function CodeRainField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hero-glow-a" />
      <span className="hero-glow-b" />
      <span className="hero-circuit" />

      <div className="absolute inset-0">
        {rainColumns.map((col, i) => (
          <div
            key={i}
            className="rain-col absolute top-0 font-mono text-xs font-bold"
            style={{
              left: `${col.left}%`,
              color: col.color,
              animationDuration: `${col.duration}s`,
              animationDelay: `${col.delay}s`,
            }}
          >
            {col.chars.map((c, j) => (
              <div key={j} style={{ opacity: 1 - j / col.chars.length }}>
                {c}
              </div>
            ))}
          </div>
        ))}
      </div>

      <span className="hero-scanbeam" />
      <span className="hero-dots-wd" />
      <span className="hero-vignette" />
    </div>
  );
}

export default function WebDevelopment() {
  const [activeSolution, setActiveSolution] = useState(0);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        /* ---- Hero: two independently drifting glow blobs ---- */
        .hero-glow-a {
          position: absolute;
          width: 44rem;
          height: 44rem;
          top: 50%;
          left: 50%;
          margin: -22rem 0 0 -22rem;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.35;
          background: radial-gradient(circle, ${theme.accent}, transparent 65%);
          animation: glowA 22s ease-in-out infinite;
        }
        @keyframes glowA {
          0%   { transform: translate(-11rem, -5rem) scale(1); }
          33%  { transform: translate(9rem, -3rem) scale(1.2); }
          66%  { transform: translate(3rem, 8rem) scale(0.85); }
          100% { transform: translate(-11rem, -5rem) scale(1); }
        }
        .hero-glow-b {
          position: absolute;
          width: 30rem;
          height: 30rem;
          top: 50%;
          left: 50%;
          margin: -15rem 0 0 -15rem;
          border-radius: 9999px;
          filter: blur(100px);
          opacity: 0.25;
          background: radial-gradient(circle, ${theme.gold}, transparent 65%);
          animation: glowB 28s ease-in-out infinite;
        }
        @keyframes glowB {
          0%   { transform: translate(8rem, 6rem) scale(1); }
          40%  { transform: translate(-9rem, 2rem) scale(0.9); }
          75%  { transform: translate(-2rem, -8rem) scale(1.15); }
          100% { transform: translate(8rem, 6rem) scale(1); }
        }

        /* ---- Hero: faint circuit-trace grid, slowly panning ---- */
        .hero-circuit {
          position: absolute;
          inset: -10%;
          opacity: 0.07;
          background-image:
            linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
          background-size: 64px 64px;
          animation: circuitPan 40s linear infinite;
        }
        @keyframes circuitPan {
          from { background-position: 0 0, 0 0; }
          to { background-position: -64px -64px, -64px -64px; }
        }

        /* ---- Hero: falling code-rain columns ---- */
        .rain-col {
          animation-name: rainFall;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          opacity: 0.5;
          line-height: 1.4;
        }
        @keyframes rainFall {
          0%   { transform: translateY(-40%); opacity: 0; }
          10%  { opacity: 0.55; }
          90%  { opacity: 0.55; }
          100% { transform: translateY(140vh); opacity: 0; }
        }

        /* ---- Hero: horizontal scan-beam sweeping down the section ---- */
        .hero-scanbeam {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, ${theme.accent}, transparent);
          box-shadow: 0 0 20px 4px ${theme.accent};
          opacity: 0.5;
          animation: scanSweep 6s ease-in-out infinite;
        }
        @keyframes scanSweep {
          0%   { top: 0%; opacity: 0; }
          10%  { opacity: 0.6; }
          50%  { top: 100%; opacity: 0.6; }
          60%  { opacity: 0; }
          100% { top: 0%; opacity: 0; }
        }

        .hero-dots-wd {
          position: absolute;
          inset: -10px;
          opacity: 0.05;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px);
          background-size: 30px 30px;
          animation: dotsDriftWd 46s linear infinite;
        }
        @keyframes dotsDriftWd {
          from { background-position: 0 0; }
          to { background-position: -300px 300px; }
        }

        /* ---- Hero: vignette so text stays readable over the rain ---- */
        .hero-vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 30%, ${theme.heroBg} 85%);
        }

        .card-glow {
          pointer-events: none;
          position: absolute;
          top: -3rem;
          right: -3rem;
          width: 9rem;
          height: 9rem;
          border-radius: 9999px;
          filter: blur(30px);
          background: ${theme.accent};
          opacity: 0.08;
          transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
        .card-glow-dark {
          pointer-events: none;
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 15% 15%, rgba(230,57,70,0.22), transparent 55%),
            repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 2px, transparent 2px, transparent 20px);
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-glow-a, .hero-glow-b, .hero-circuit, .rain-col,
          .hero-scanbeam, .hero-dots-wd { animation: none !important; }
        }
      `}</style>

      {/* ================= HERO — fully custom bg, no card/device mock ================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-44 pb-28 md:pt-56 md:pb-40"
        style={{ backgroundColor: theme.heroBg }}
      >
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <CodeRainField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Eyebrow light center>Web Development</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="We build" className="text-white" />{" "}
              <span style={{ color: theme.accent }}>fast, scalable</span>
              <br />
              <SplitText text="websites that convert" delay={0.25} className="text-white" />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              High-performance websites & scalable web apps built for growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Start a Project
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/work"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  View Our Work
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-20" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-center">
          {stats.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <h3 className="text-4xl font-extrabold" style={{ color: theme.accent }}>{s.n}</h3>
              <p className="mt-2" style={{ color: theme.inkMuted }}>{s.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= SOLUTIONS (dark band, interactive spotlight) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] translate-x-1/3 -translate-y-1/3 transition-colors duration-500"
          style={{ backgroundColor: solutionAccents[activeSolution] }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="max-w-2xl mb-16">
            <Eyebrow light>What We Build</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
              <SplitText text="Our web" />{" "}
              <SplitText text="solutions" delay={0.2} className="inline" as="span" />
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              We design and develop fast, secure and scalable websites using
              modern technologies — from startups to enterprises.
            </p>
          </RevealSection>

          <RevealSection
            delay={0.1}
            className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-0 rounded-3xl border border-white/10 overflow-hidden"
          >
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              {solutions.map((item, i) => {
                const isActive = i === activeSolution;
                return (
                  <button
                    key={item.no}
                    data-cursor="hover"
                    onClick={() => setActiveSolution(i)}
                    onMouseEnter={() => setActiveSolution(i)}
                    className="relative flex items-center gap-3 text-left px-6 py-5 shrink-0 w-full transition-colors duration-300"
                    style={{ backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent" }}
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-transform duration-300 origin-left"
                      style={{ backgroundColor: solutionAccents[i], transform: isActive ? "scaleX(1)" : "scaleX(0)" }}
                    />
                    <span
                      className="font-mono text-xs font-semibold transition-colors duration-300"
                      style={{ color: isActive ? solutionAccents[i] : "rgba(255,255,255,0.35)" }}
                    >
                      {item.no}
                    </span>
                    <span
                      className="text-sm font-semibold whitespace-nowrap transition-colors duration-300"
                      style={{ color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}
                    >
                      {item.title}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative p-10 md:p-14 min-h-[280px] flex items-center" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
              <span className="pointer-events-none absolute -right-10 -bottom-10 opacity-[0.06]" style={{ color: "#FFFFFF" }}>
                {(() => {
                  const ActiveIcon = solutions[activeSolution].icon;
                  return <ActiveIcon size={220} strokeWidth={1} />;
                })()}
              </span>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSolution}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="relative z-10 max-w-lg"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${solutionAccents[activeSolution]}26`, color: solutionAccents[activeSolution] }}
                  >
                    {(() => {
                      const ActiveIcon = solutions[activeSolution].icon;
                      return <ActiveIcon size={26} />;
                    })()}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {solutions[activeSolution].title}
                  </h3>
                  <p className="mt-4 text-white/60 leading-relaxed">
                    {solutions[activeSolution].desc}
                  </p>
                  <Link
                    to="/contact"
                    data-cursor="hover"
                    style={{ color: solutionAccents[activeSolution] }}
                    className="mt-6 inline-flex items-center gap-2 font-semibold hover:brightness-110 transition-all w-fit"
                  >
                    Get started
                    <ArrowUpRight size={16} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ================= FEATURES (bento, light) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.surfaceAlt }}>
        <DotGrid />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow center>Why Choose Us</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Built for" /> <span style={{ color: theme.accent }}>performance</span>{" "}
              <SplitText text="and scale" delay={0.15} />
            </h2>
            <p className="mt-4 text-lg" style={{ color: theme.inkMuted }}>
              Every build ships with speed, security and scalability at the core.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[1fr]"
          >
            {features.map((item, i) => (
              <motion.div
                variants={staggerItem}
                key={item.title}
                data-cursor="hover"
                whileHover={{ y: -6, scale: 1.025, rotate: i % 2 === 0 ? -0.6 : 0.6 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className={`group relative overflow-hidden rounded-3xl p-8 border shadow-sm hover:shadow-2xl flex flex-col ${
                  item.highlight ? "sm:col-span-2" : ""
                }`}
                style={{
                  backgroundColor: item.highlight ? theme.ink : "#FFFFFF",
                  borderColor: item.highlight ? theme.ink : theme.border,
                }}
              >
                {item.highlight ? <span className="card-glow-dark" /> : <span className="card-glow" />}
                <div className={`relative ${item.highlight ? "flex items-start gap-6" : ""}`}>
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-xl mb-6 shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{
                      backgroundColor: item.highlight ? "rgba(230,57,70,0.18)" : theme.surfaceAlt,
                      color: item.highlight ? "#FF8A93" : theme.accent,
                    }}
                  >
                    <item.icon size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold transition-colors" style={{ color: item.highlight ? "#FFFFFF" : theme.ink }}>
                      {item.title}
                    </h3>
                    <p className="mt-3 leading-relaxed" style={{ color: item.highlight ? "rgba(255,255,255,0.65)" : theme.inkMuted }}>
                      {item.desc}
                    </p>
                    <span
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      style={{ color: item.highlight ? "#FF8A93" : theme.accent }}
                    >
                      Learn more <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= TECH STACK (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBandDeep }}>
        <DotGrid light />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center mb-14">
            <Eyebrow light center>Our Toolkit</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Technology Stack</h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center"
          >
            {techStack.map((tech) => (
              <motion.div
                key={tech}
                variants={staggerItem}
                whileHover={{ scale: 1.08, y: -4 }}
                data-cursor="hover"
                className="p-6 rounded-xl border font-semibold text-white transition-colors"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                {tech}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= PROCESS (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>How We Work</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.ink }}>Our Process</h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {process.map((item, i) => (
              <motion.div
                key={item.step}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="relative p-6 rounded-2xl border shadow-sm"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold mb-4"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-semibold mb-2" style={{ color: theme.ink }}>{item.step}</h3>
                <p className="text-sm" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FAQ (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surfaceAlt }}>
        <div className="max-w-4xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>Questions</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.ink }}>FAQs</h2>
          </RevealSection>
          {faqs.map((item, i) => (
            <motion.div
              key={item.q}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="mb-5 p-6 rounded-xl border shadow-sm"
              style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
            >
              <h3 className="font-semibold mb-2" style={{ color: theme.ink }}>{item.q}</h3>
              <p style={{ color: theme.inkMuted }}>{item.a}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -left-6 -top-10 text-[14rem] font-extrabold leading-none"
          style={{ color: "rgba(255,255,255,0.04)" }}
        >
          {"</>"}
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Get In Touch</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Ready to start your" />
            <span className="block">
              <SplitText text="website" delay={0.15} /> <span style={{ color: theme.accent }}>project?</span>
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Let's build something amazing together.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Us
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}